import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Eye, EyeOff, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';

// =============================================
// PAGE DE CONNEXION BACK-OFFICE - SIMPLE
// Logo + Formulaire de connexion centré
// =============================================

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    loadLogo();
  }, []);

  const loadLogo = async () => {
    try {
      const res = await api.get('/settings/theme').catch(() => ({ data: null }));
      if (res.data?.logo?.darkUrl) {
        setLogoUrl(res.data.logo.darkUrl);
      }
    } catch (error) {
      console.error('Error loading logo:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const user = await login(identifier, password);
      toast.success(`Bienvenue, ${user.first_name || user.username} !`);
      redirectUser(user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Identifiants incorrects');
    } finally {
      setLoading(false);
    }
  };

  const redirectUser = (user) => {
    if (user.account_status === 'pending_approval') {
      navigate('/pending-approval', { replace: true });
      return;
    }
    
    switch (user.role) {
      case 'agency':
        navigate('/cms', { replace: true });
        break;
      case 'affiliate':
        navigate('/affiliate/dashboard', { replace: true });
        break;
      case 'team_member':
        if (user.team_member_type === 'affiliate') {
          navigate('/affiliate/dashboard', { replace: true });
        } else {
          navigate('/dashboard/team', { replace: true });
        }
        break;
      case 'customer_service':
        navigate('/dashboard/customer-service', { replace: true });
        break;
      default:
        navigate('/dashboard/client', { replace: true });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
      </div>

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          {logoUrl ? (
            <img 
              src={logoUrl} 
              alt="MSC L'Agence" 
              className="h-16 mx-auto mb-4 object-contain"
            />
          ) : (
            <div className="mb-4">
              <h1 className="text-3xl font-black text-white tracking-tight">
                MSC <span className="text-cyan-400">L'AGENCE</span>
              </h1>
            </div>
          )}
          <div className="flex items-center justify-center gap-2 text-slate-400 text-sm">
            <Lock className="w-4 h-4" />
            <span>Back-Office</span>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="identifier" className="text-white/80 text-sm font-medium">
                Email
              </Label>
              <Input
                id="identifier"
                type="email"
                placeholder="votre@email.com"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                className="h-12 bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl focus:border-cyan-400 focus:ring-cyan-400/20"
                required
                autoComplete="email"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/80 text-sm font-medium">
                Mot de passe
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl pr-12 focus:border-cyan-400 focus:ring-cyan-400/20"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-xl font-semibold text-white bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 shadow-lg shadow-cyan-500/25 transition-all duration-200"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'Se connecter'
              )}
            </Button>
          </form>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-slate-500 text-xs">
            © {new Date().getFullYear()} MSC L'Agence · Tous droits réservés
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
